import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { SelectOption } from '~/shared/utils/types';
import FilterRemoveIcon from '~/shared/assets/icons/filter-remove.svg';
import PageContainer from '~/shared/components/PageContainer';
import Typography from '~/shared/components/Typography';

import DashboardCharts from '../DashboardCharts';
import DashboardFilter from '../DashboardFilter';
import {
  apiFilterKey,
  utilizationDateFilterKey,
  utilizationFullGraphKey,
  utilizationRefreshStateKey,
} from '../common/serachParamKeys';
import useUtilizationQueryParams from '../common/useUtilizationQueryParams';

import FleetManagerTable from './FleetManagerTable';
import { getApiFilterFromSelectOption } from './helpers';

const dataLimit = 50;
const sortKey = 'sort_field';

export default function FleetManagerPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeDateFilter = searchParams.get(utilizationDateFilterKey);
  const activeRefreshState = searchParams.get(utilizationRefreshStateKey);
  const apiFilters = searchParams.get(apiFilterKey);

  const sortField: { field: string; dir: 'desc' | 'asc' } = JSON.parse(
    searchParams.get(sortKey) as string
  );

  const [pageOffset, setPageOffset] = useState('0');

  const { utilizationQueryUrl, onRefetchUtilizationQuery } = useUtilizationQueryParams({
    utilizationDateFilterSearchParam: activeDateFilter,
    showFullGraphSearchParam: searchParams.get(utilizationFullGraphKey),
    apiFiltersSearchParam: apiFilters,
    refreshStateSearchParams: activeRefreshState,
  });

  const qs = new URLSearchParams({
    sortBy: JSON.stringify({
      field: sortField?.field || 'volumeId',
      ascending: sortField?.dir !== 'desc',
    }),
    limit: dataLimit.toString(),
    offset: pageOffset,
  });

  const filters = JSON.parse(apiFilters || '[]') as SelectOption[];
  const parsedFiltersSearchParams: string[] = filters?.map((f: SelectOption) =>
    getApiFilterFromSelectOption(f)
  );

  const savingsQs = new URLSearchParams([...parsedFiltersSearchParams.map((f) => ['filters', f])]);

  const savingsQueryUrl = `/api/v1/volumes/savings-stats?${savingsQs.toString()}`;

  return (
    <PageContainer>
      <div className="flex gap-[48px] items-start">
        <DashboardFilter
          filters={filters}
          onFilterChange={(value: SelectOption[]) => {
            setPageOffset('0');

            qs.delete('filters');
            setSearchParams(
              (params) => {
                params.set(apiFilterKey, JSON.stringify(value));
                return params;
              },
              { replace: true }
            );
          }}
        />

        <button
          onClick={() =>
            setSearchParams((params) => {
              params.delete(apiFilterKey);
              return params;
            })
          }
          className="flex gap-[10px] items-center h-[48px]"
        >
          <FilterRemoveIcon className="w-[24px] aspect-square fill-primary-50" />
          <Typography variant="p3" color="text-primary-50">
            Clear selection
          </Typography>
        </button>
      </div>

      <DashboardCharts
        utilizationQueryUrl={utilizationQueryUrl}
        savingsQueryUrl={savingsQueryUrl}
        activeDateFilter={activeDateFilter || 'Last 3 hours'}
        activeRefreshState={activeRefreshState || ''}
        onClickRefresh={onRefetchUtilizationQuery}
        onSelectRefreshState={(item) => {
          setSearchParams(
            (params) => {
              params.set(utilizationRefreshStateKey, item.title);
              return params;
            },
            { replace: true }
          );
        }}
        onSelectDateFilter={(filter) => {
          setSearchParams(
            (params) => {
              params.set(utilizationDateFilterKey, filter.title);
              return params;
            },
            { replace: true }
          );
        }}
      />

      <FleetManagerTable
        qs={qs}
        savingsChartQueryKey={savingsQueryUrl}
        utilizationChartQueryKey={utilizationQueryUrl}
        pageOffset={pageOffset}
        setPageOffset={setPageOffset}
        sortField={sortField}
        filters={filters}
      />
    </PageContainer>
  );
}
